import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Container, Card, Badge, Button, Row, Col, Form, 
  ListGroup, Alert, Modal, ProgressBar, Tooltip, OverlayTrigger, Dropdown
} from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { taskService, userService, departmentService } from '../../services/api';
import './TaskDetail.css';

// Translation service implementation
const translateService = async (text, targetLang) => {
  try {
    const response = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`);

    if (!response.ok) {
      throw new Error(`Translation failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.responseData?.translatedText) {
      return data.responseData.translatedText;
    } else {
      throw new Error('Invalid translation response format');
    }
  } catch (error) {
    console.error('Translation error:', error);
    throw error;
  }
};

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isManager, isAdmin } = useAuth();
  
  const [task, setTask] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [translatedDescription, setTranslatedDescription] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const [newComment, setNewComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    priority: '',
    due_date: '',
    department_id: ''
  });

  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Function to check and update available voices
  const updateAvailableVoices = () => {
    const voices = window.speechSynthesis.getVoices();
    const voiceList = voices.map(voice => ({
      name: voice.name,
      lang: voice.lang,
      default: voice.default,
      localService: voice.localService
    }));
    console.log('Available voices:', voiceList);
    setAvailableVoices(voices);
    
    // Try to find and set a default voice for the current language
    if (selectedLanguage) {
      const languageMap = {
        'kn': 'kn-IN',
        'hi': 'hi-IN',
        'bn': 'bn-IN',
        'ta': 'ta-IN',
        'te': 'te-IN',
        'ml': 'ml-IN',
        'mr': 'mr-IN',
        'gu': 'gu-IN',
        'pa': 'pa-IN',
      };
      const mappedLang = languageMap[selectedLanguage] || selectedLanguage;
      const voice = voices.find(v => v.lang.startsWith(mappedLang)) ||
                   voices.find(v => v.lang.startsWith(selectedLanguage));
      setSelectedVoice(voice || null);
    }
  };

  // Update voices when they change
  useEffect(() => {
    const handleVoicesChanged = () => {
      updateAvailableVoices();
    };

    window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
    // Initial check for voices
    updateAvailableVoices();

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
    };
  }, [selectedLanguage]);

  // Add this function to automatically select the best voice for a language
  const selectBestVoiceForLanguage = (languageCode) => {
    if (!languageCode || availableVoices.length === 0) return null;

    // First, try to find a voice that exactly matches the language code
    let voice = availableVoices.find(v => v.lang.toLowerCase() === languageCode.toLowerCase());

    // If no exact match, try to find a voice that starts with the language code
    if (!voice) {
      voice = availableVoices.find(v => v.lang.toLowerCase().startsWith(languageCode.toLowerCase()));
    }

    // If still no match, try to match just the primary language code (e.g., 'en' for 'en-US')
    if (!voice) {
      const primaryLang = languageCode.split('-')[0].toLowerCase();
      voice = availableVoices.find(v => v.lang.toLowerCase().startsWith(primaryLang));
    }

    return voice || null;
  };

  // Update the translateDescription function
  const translateDescription = async (targetLang) => {
    if (!task?.description) return;
    
    try {
      setIsTranslating(true);
      setSelectedLanguage(targetLang);
      
      // Your existing translation logic here
      const translated = await translateService(task.description, targetLang);
      setTranslatedDescription(translated);
      
      // Automatically select the best voice for the target language
      const bestVoice = selectBestVoiceForLanguage(targetLang);
      setSelectedVoice(bestVoice);
      
    } catch (error) {
      console.error('Translation error:', error);
      setError('Failed to translate text. Please try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  // Language mapping for voices
  const languageVoiceMap = {
    'kn': 'kn-IN',
    'hi': 'hi-IN',
    'bn': 'bn-IN',
    'ta': 'ta-IN',
    'te': 'te-IN',
    'ml': 'ml-IN',
    'mr': 'mr-IN',
    'gu': 'gu-IN',
    'pa': 'pa-IN',
    'es': 'es-ES',
    'fr': 'fr-FR',
    'de': 'de-DE',
    'it': 'it-IT',
    'pt': 'pt-PT',
    'ru': 'ru-RU',
    'zh': 'zh-CN',
    'ja': 'ja-JP',
    'ko': 'ko-KR',
    'ar': 'ar-SA'
  };

  // Update the speakText function to use language mapping
  const speakText = (text, lang) => {
    if (!text || isSpeaking) return;
    
    setIsSpeaking(true);
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Get the mapped language code
    const mappedLang = languageVoiceMap[lang] || lang;
    utterance.lang = mappedLang;

    // Try to find a voice for the language
    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find(v => v.lang.startsWith(mappedLang)) ||
                 voices.find(v => v.lang.startsWith(lang)) ||
                 voices.find(v => v.default);
    
    if (voice) {
      utterance.voice = voice;
    }
    
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => {
      setIsSpeaking(false);
      setError('Failed to play speech. Please try again.');
    };
    
    window.speechSynthesis.speak(utterance);
  };

  // Function to check available voices
  const checkAvailableVoices = () => {
    const voices = window.speechSynthesis.getVoices();
    console.log('Available voices:', voices.map(voice => ({
      name: voice.name,
      lang: voice.lang,
      default: voice.default,
      localService: voice.localService
    })));
  };

  // Add this to the existing useEffect that handles voices
  useEffect(() => {
    const handleVoicesChanged = () => {
      window.speechSynthesis.getVoices();
      checkAvailableVoices(); // Log available voices when they change
    };

    window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
    // Check voices immediately as well
    checkAvailableVoices();

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
    };
  }, []);

  // Function to stop speech
  const stopSpeech = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  // Language options with their codes
  const languages = [
    { code: 'kn', name: 'ಕನ್ನಡ (Kannada)' },
    { code: 'hi', name: 'हिंदी (Hindi)' },
    { code: 'bn', name: 'বাংলা (Bengali)' },
    { code: 'ta', name: 'தமிழ் (Tamil)' },
    { code: 'te', name: 'తెలుగు (Telugu)' },
    { code: 'ml', name: 'മലയാളം (Malayalam)' },
    { code: 'mr', name: 'मराठी (Marathi)' },
    { code: 'gu', name: 'ગુજરાતી (Gujarati)' },
    { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)' },
    { code: 'es', name: 'Español (Spanish)' },
    { code: 'fr', name: 'Français (French)' },
    { code: 'de', name: 'Deutsch (German)' },
    { code: 'it', name: 'Italiano (Italian)' },
    { code: 'pt', name: 'Português (Portuguese)' },
    { code: 'ru', name: 'Русский (Russian)' },
    { code: 'zh', name: '中文 (Chinese)' },
    { code: 'ja', name: '日本語 (Japanese)' },
    { code: 'ko', name: '한국어 (Korean)' },
    { code: 'ar', name: 'العربية (Arabic)' }
  ];

  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        setLoading(true);
        
        // Fetch task details
        const taskResponse = await taskService.getTaskById(id);
        setTask(taskResponse.data?.data || null);
        setNewStatus(taskResponse.data?.data?.status || '');
        setEditForm({
          title: taskResponse.data?.data?.title || '',
          description: taskResponse.data?.data?.description || '',
          priority: taskResponse.data?.data?.priority || '',
          due_date: taskResponse.data?.data?.due_date ? taskResponse.data.data.due_date.split('T')[0] : '',
          department_id: taskResponse.data?.data?.department_id || ''
        });
        
        // Fetch task assignments
        const assignmentsResponse = await taskService.getTaskAssignments(id);
        setAssignments(assignmentsResponse.data?.data || []);
        
        // Fetch task comments
        const commentsResponse = await taskService.getTaskComments(id);
        setComments(commentsResponse.data?.data || []);
        
        // Fetch users for assignment (managers only)
        if (isManager) {
          const usersResponse = await userService.getAllUsers();
          setUsers(usersResponse.data?.data || []);
        }
        
        setError('');
      } catch (err) {
        console.error('Error fetching task data:', err);
        setError('Failed to load task data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTaskData();
  }, [id, isManager]);

  const handleStatusChange = async () => {
    try {
      if (newStatus === 'request_approval' && currentUser.role === 'employee') {
        // Employee requesting completion approval
        await taskService.updateTask(id, { status: 'awaiting_approval' });
        setTask(prev => ({ ...prev, status: 'awaiting_approval' }));
        setError('Your completion request has been sent to admin for approval.');
      } else if (newStatus === 'completed' && currentUser.role === 'admin') {
        // Admin approving task completion
        await taskService.updateTask(id, { status: 'completed' });
        setTask(prev => ({ ...prev, status: 'completed' }));
        setError('Task has been approved and marked as completed.');
      } else if (newStatus === 'in_progress' && currentUser.role === 'employee') {
        // Employee starting the task
        await taskService.updateTask(id, { status: 'in_progress' });
        setTask(prev => ({ ...prev, status: 'in_progress' }));
        setError('');
      } else if (currentUser.role === 'admin') {
        // Admin can update any status
        await taskService.updateTask(id, { status: newStatus });
        setTask(prev => ({ ...prev, status: newStatus }));
        setError('');
      } else {
        // Other status updates
        await taskService.updateTask(id, { status: newStatus });
        setTask(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status. Please try again.');
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) return;
    
    try {
      const response = await taskService.addTaskComment(id, newComment);
      
      // Add the new comment to the list
      if (response.data?.success) {
        setComments(prevComments => [
          ...prevComments,
          {
            id: response.data.data.id,
            task_id: id,
            user_id: currentUser.id,
            username: currentUser.username,
            comment: newComment,
            created_at: new Date().toISOString()
          }
        ]);
        
        // Clear the comment input
        setNewComment('');
      } else {
        throw new Error('Failed to add comment');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment. Please try again.');
    }
  };

  const handleShowAssignModal = () => {
    const currentAssigneeIds = assignments.map(assignment => assignment.user_id.toString());
    setSelectedUsers(currentAssigneeIds);
    setShowAssignModal(true);
  };

  const handleAssignTask = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      const response = await taskService.assignTask(id, {
        userId: selectedUsers.map(userId => parseInt(userId))
      });
      
      if (response.data?.success) {
        // Fetch updated assignments
        const assignmentsResponse = await taskService.getTaskAssignments(id);
        setAssignments(assignmentsResponse.data?.data || []);
        
        // Close the modal and reset selection
        setShowAssignModal(false);
        setError('');
      } else {
        throw new Error('Failed to assign task');
      }
    } catch (err) {
      console.error('Error assigning task:', err.response?.data?.message || err.message);
      setError(err.response?.data?.message || 'Failed to assign task. Please try again.');
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (isManager && task?.department_id) {
          const response = await departmentService.getDepartmentMembers(task.department_id);
          setUsers(response.data?.data || []);
        }
      } catch (err) {
        console.error('Error fetching department users:', err);
        setError('Failed to load department users.');
      }
    };

    fetchUsers();
  }, [isManager, task?.department_id]);

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async () => {
    try {
      await taskService.updateTask(id, editForm);
      
      // Update the task in state
      setTask(prev => ({
        ...prev,
        ...editForm
      }));
      
      // Close the modal
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Failed to update task. Please try again.');
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'in_progress': return 'primary';
      case 'awaiting_approval': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'low': return 'info';
      case 'medium': return 'secondary';
      case 'high': return 'warning';
      case 'urgent': return 'danger';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    return new Date(dateTimeString).toLocaleString();
  };

  const getProgressValue = (status) => {
    switch (status) {
      case 'pending': return 0;
      case 'in_progress': return 50;
      case 'awaiting_approval': return 75;
      case 'completed': return 100;
      case 'cancelled': return 0;
      default: return 0;
    }
  };

  const renderTooltip = (text) => (
    <Tooltip>{text}</Tooltip>
  );

  return (
    <Container fluid className="task-detail-container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <h2 className="mb-0 me-3">Task Details</h2>
          <Badge 
            bg={getStatusBadgeVariant(task?.status)} 
            className="status-badge"
          >
            {task?.status === 'awaiting_approval' ? 'Waiting for Approval' : 
             !task?.status || task?.status === 'pending' ? 'To Do' : 
             task?.status.split('_')
               .map(word => word.charAt(0).toUpperCase() + word.slice(1))
               .join(' ')}
          </Badge>
        </div>
        <div>
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/tasks')}
            className="me-2"
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Tasks
          </Button>
          {isManager && (
            <Button 
              variant="primary" 
              onClick={() => setShowEditModal(true)}
            >
              <i className="fas fa-edit me-2"></i>
              Edit Task
            </Button>
          )}
        </div>
      </div>
      
      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
      
      {loading ? (
        <div className="loading-container text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading task details...</p>
        </div>
      ) : task ? (
        <Row>
          <Col lg={8}>
            <Card className="task-card mb-4 shadow-sm">
              <Card.Header className="bg-white border-bottom-0 pt-4">
                <h3 className="mb-3">{task.title}</h3>
                <ProgressBar 
                  now={getProgressValue(task.status)} 
                  variant={getStatusBadgeVariant(task.status)}
                  className="mb-3"
                />
              </Card.Header>
              <Card.Body>
                <div className="description-section mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <i className="fas fa-align-left"></i>
                      Description
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-primary" size="sm" id="language-dropdown">
                          <i className="fas fa-language me-2"></i>
                          Translate
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {languages.map(lang => (
                            <Dropdown.Item 
                              key={lang.code}
                              onClick={() => translateDescription(lang.code)}
                              active={selectedLanguage === lang.code}
                            >
                              {lang.name}
                            </Dropdown.Item>
                          ))}
                          {selectedLanguage && (
                            <>
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={() => {
                                setTranslatedDescription('');
                                setSelectedLanguage('');
                              }}>
                                Show Original
                              </Dropdown.Item>
                            </>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                      {(translatedDescription || task.description) && (
                        <div className="d-flex gap-2">
                          <Button 
                            variant="outline-success" 
                            size="sm"
                            onClick={() => speakText(
                              translatedDescription || task.description,
                              selectedLanguage || 'en'
                            )}
                            disabled={isSpeaking}
                          >
                            <i className={`fas ${isSpeaking ? 'fa-volume-up' : 'fa-play'} me-2`}></i>
                            {isSpeaking ? 'Speaking...' : 'Listen'}
                          </Button>
                          {isSpeaking && (
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={stopSpeech}
                            >
                              <i className="fas fa-stop me-2"></i>
                              Stop
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="description-content bg-light p-3 rounded">
                    {isTranslating ? (
                      <div className="text-center py-3">
                        <span className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Translating...</span>
                        </span>
                        <div className="mt-2 text-muted">Translating...</div>
                      </div>
                    ) : (
                      <div className="task-description">
                        {translatedDescription || task.description || 'No description provided'}
                        {translatedDescription && (
                          <div className="mt-2 text-muted">
                            <small>
                              <i className="fas fa-info-circle me-1"></i>
                              Translated to {languages.find(l => l.code === selectedLanguage)?.name}
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status update section with improved UI */}
                <div className="status-section bg-light p-4 rounded border-start border-3 border-primary">
                  <h5 className="mb-3">
                    <i className="fas fa-tasks me-2"></i>
                    Update Task Status
                  </h5>
                  
                  {currentUser.role === 'employee' && task.status === 'in_progress' && (
                    <Alert variant="info" className="mb-3">
                      <i className="fas fa-info-circle me-2"></i>
                      When you complete this task, select "Request Completion Approval" to notify the administrator.
                    </Alert>
                  )}

                  {currentUser.role === 'admin' && task.status === 'awaiting_approval' && (
                    <Alert variant="warning" className="mb-3">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      This task is waiting for your approval. Please review and either approve to complete or reject.
                    </Alert>
                  )}
                  
                  <div className="d-flex">
                    <Form.Select 
                      value={newStatus} 
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="me-2"
                    >
                      {currentUser.role === 'employee' ? (
                        <>
                          <option value="pending">To Do</option>
                          <option value="in_progress">Start Task</option>
                          <option value="request_approval">Request Completion Approval</option>
                        </>
                      ) : currentUser.role === 'admin' ? (
                        <>
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="awaiting_approval">Awaiting Approval</option>
                          <option value="completed">Approve & Complete</option>
                          <option value="cancelled">Cancel Task</option>
                        </>
                      ) : (
                        <>
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </>
                      )}
                    </Form.Select>
                    <Button 
                      variant="primary" 
                      onClick={handleStatusChange}
                      disabled={newStatus === task.status}
                    >
                      <i className="fas fa-check me-2"></i>
                      Update Status
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Comments section with improved UI */}
            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-comments me-2"></i>
                  Comments
                </h5>
              </Card.Header>
              <Card.Body>
                <ListGroup variant="flush" className="mb-4">
                  {comments.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <i className="fas fa-comment-slash fa-2x mb-3"></i>
                      <p>No comments yet. Be the first to comment!</p>
                    </div>
                  ) : (
                    comments.map(comment => (
                      <ListGroup.Item key={comment.id} className="px-0">
                        <div className="d-flex align-items-start">
                          <div className="comment-avatar me-3">
                            <div className="avatar-placeholder">
                              {comment.username.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>{comment.username}</strong>
                              <small className="text-muted">
                                {formatDateTime(comment.created_at)}
                              </small>
                            </div>
                            <p className="mb-0">{comment.comment}</p>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))
                  )}
                </ListGroup>
                
                <Form onSubmit={handleCommentSubmit} className="comment-form">
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      required
                      className="comment-input"
                    />
                  </Form.Group>
                  <Button type="submit" variant="primary">
                    <i className="fas fa-paper-plane me-2"></i>
                    Add Comment
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Task Timeline */}
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-history me-2"></i>
                  Task Timeline
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-marker bg-success"></div>
                    <div className="timeline-content">
                      <p className="mb-0"><strong>Task Created</strong></p>
                      <small className="text-muted">{formatDateTime(task.created_at)}</small>
                    </div>
                  </div>
                  {task.status !== 'pending' && (
                    <div className="timeline-item">
                      <div className="timeline-marker bg-primary"></div>
                      <div className="timeline-content">
                        <p className="mb-0"><strong>Started</strong></p>
                        <small className="text-muted">Task in progress</small>
                      </div>
                    </div>
                  )}
                  {task.status === 'awaiting_approval' && (
                    <div className="timeline-item">
                      <div className="timeline-marker bg-warning"></div>
                      <div className="timeline-content">
                        <p className="mb-0"><strong>Awaiting Approval</strong></p>
                        <small className="text-muted">Pending admin review</small>
                      </div>
                    </div>
                  )}
                  {task.status === 'completed' && (
                    <div className="timeline-item">
                      <div className="timeline-marker bg-success"></div>
                      <div className="timeline-content">
                        <p className="mb-0"><strong>Completed</strong></p>
                        <small className="text-muted">{formatDateTime(task.updated_at)}</small>
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Task Details Card */}
            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-info-circle me-2 text-primary"></i>
                  Task Details
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <ListGroup variant="flush">
                  <ListGroup.Item className="border-start border-4 border-primary">
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-users me-3 text-primary"></i>
                      <h6 className="mb-0">Assigned To</h6>
                    </div>
                    {assignments.length > 0 ? (
                      <div className="ms-4">
                        {assignments.map((assignment, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">
                            <div className="avatar-placeholder me-2" style={{ width: '30px', height: '30px', fontSize: '0.8rem' }}>
                              {assignment.username.charAt(0).toUpperCase()}
                            </div>
                            <span>{assignment.username}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted ms-4 mb-0">No assignments yet</p>
                    )}
                  </ListGroup.Item>

                  <ListGroup.Item className="border-start border-4 border-info">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-building me-3 text-info"></i>
                      <div>
                        <h6 className="mb-1">Department</h6>
                        <p className="mb-0">{task?.department_name || 'Not assigned'}</p>
                      </div>
                    </div>
                  </ListGroup.Item>

                  <ListGroup.Item className="border-start border-4 border-warning">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-calendar me-3 text-warning"></i>
                      <div>
                        <h6 className="mb-1">Important Dates</h6>
                        <div className="d-flex flex-wrap gap-3">
                          <div>
                            <small className="text-muted d-block">Start Date</small>
                            <span>{task?.created_at ? formatDate(task.created_at) : 'Not set'}</span>
                          </div>
                          <div>
                            <small className="text-muted d-block">Due Date</small>
                            <span className={
                              task?.due_date && 
                              task.status !== 'completed' && 
                              new Date(task.due_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) 
                                ? 'text-danger' 
                                : ''
                            }>
                              {task?.due_date ? formatDate(task.due_date) : 'Not set'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>

                  <ListGroup.Item className="border-start border-4 border-danger">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-flag me-3 text-danger"></i>
                      <div>
                        <h6 className="mb-1">Priority Level</h6>
                        <Badge 
                          bg={getPriorityBadgeVariant(task?.priority)}
                          className="rounded-pill"
                          style={{ fontSize: '0.9rem', padding: '0.5em 1em' }}
                        >
                          {task?.priority?.toUpperCase() || 'NOT SET'}
                        </Badge>
                      </div>
                    </div>
                  </ListGroup.Item>

                  {isManager && (
                    <ListGroup.Item className="bg-light">
                      <Button 
                        variant="primary" 
                        className="w-100"
                        onClick={handleShowAssignModal}
                      >
                        <i className="fas fa-user-plus me-2"></i>
                        Assign Users
                      </Button>
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Task not found
        </Alert>
      )}
      
      {/* Modals with improved UI */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user-plus me-2"></i>
            Assign Users
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Users ({task?.department_name})</Form.Label>
            <div className="border rounded mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {users
                .filter(user => user.department_id === task?.department_id)
                .map(user => (
                  <div
                    key={user.id}
                    className="d-flex align-items-center p-2 border-bottom user-select-none"
                    onClick={() => {
                      const userId = user.id.toString();
                      setSelectedUsers(prev => 
                        prev.includes(userId)
                          ? prev.filter(id => id !== userId)
                          : [...prev, userId]
                      );
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={selectedUsers.includes(user.id.toString())}
                      onChange={() => {}} // Handled by div onClick
                      className="me-2"
                    />
                    {user.username}
                  </div>
                ))}
            </div>
            {users.filter(user => user.department_id === task?.department_id).length === 0 && (
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                No users found in this department
              </Alert>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignTask}
            disabled={selectedUsers.length === 0}
          >
            <i className="fas fa-check me-2"></i>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            Edit Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={editForm.title}
                onChange={handleEditFormChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={editForm.description}
                onChange={handleEditFormChange}
                className="description-input"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                name="priority"
                value={editForm.priority}
                onChange={handleEditFormChange}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="date"
                name="due_date"
                value={editForm.due_date}
                onChange={handleEditFormChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            <i className="fas fa-save me-2"></i>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TaskDetail;
